"use client";

import {
  ColumnFiltersState,
  ColumnPinningState,
  FilterFn,
  flexRender,
  getCoreRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  Header,
  PaginationState,
  Row,
  RowSelectionState,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { useEffect, useRef, useState, useMemo } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { AlertTriangle, ChevronUp, ChevronDown } from "lucide-react";
import { Table } from "@/components/ui/table";
import { Loading } from "@/components/Loading";
import { getCommonPinningStyles } from "@/lib/utils";
import BulkActions from "./BulkActions";
import { Pagination } from "./Pagination";
import { CustomTableProps } from "@/lib/types/table.types";
import useTableStore from "@/lib/stores/tableStore";
import "@/styles/globals.css";
import { cn } from "@/lib/utils";
import { Filter } from "./Filter";

const CustomTable = <T extends Record<string, any>>({
  tableId,
  columns,
  rows,
  defaultPageSize = 10,
  manualPagination = false,
  customPagination = false,
  defaultPinnedColumns,
  cardComponent,
  bulkActions,
  manualSearch,
  rightTop,
  leftTop,
  viewMode = "table",
  pageOffset = 15.5,
  scrollable = true,
  isLoading = false,
  defaultSorting,
  maxHeight,
  minHeight,
  emptyContent,
}: CustomTableProps<T>) => {
  const { setTableData, tableData } = useTableStore();

  const initialPagination = useMemo(() => {
    return (
      tableData.find((t) => t.tableId === tableId)?.pagination || {
        pageIndex: 0,
        pageSize: defaultPageSize,
      }
    );
  }, [tableId, tableData, defaultPageSize]);

  const [pagination, setPagination] =
    useState<PaginationState>(initialPagination);

  const initialRowsData = useMemo(
    () => ({
      ...rows,
      data: rows.data || [],
      rowCount: rows.rowCount || rows.data?.length || 0,
      pageCount:
        rows.pageCount ||
        Math.ceil(
          (rows.rowCount || rows.data?.length || 0) / initialPagination.pageSize
        ),
    }),
    [rows, initialPagination.pageSize]
  );

  const [rowsData, setRowsData] =
    useState<CustomTableProps<T>["rows"]>(initialRowsData);

  const [columnPinning, setColumnPinning] = useState<ColumnPinningState>(
    defaultPinnedColumns
      ? {
          left: ["select", ...(defaultPinnedColumns?.left || [])],
          right: [...(defaultPinnedColumns?.right || [])],
        }
      : {
          left: [],
          right: [],
        }
  );
  const [sorting, setSorting] = useState<SortingState>(
    tableData.find((t) => t.tableId === tableId)?.sorting ||
      defaultSorting ||
      []
  );
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(
    tableData.find((t) => t.tableId === tableId)?.columnFilters || []
  );
  const [globalFilter, setGlobalFilter] = useState<any>(
    tableData.find((t) => t.tableId === tableId)?.globalFilter || ""
  );
  const [rowSelection, setRowSelection] = useState<RowSelectionState>(
    tableData.find((t) => t.tableId === tableId)?.rowSelection || {}
  );
  const weakIncludesStringFn: FilterFn<T> = (
    srows: Row<T>,
    columnId: string,
    filterValue: any
  ) => {
    const rowValue = srows.getValue(columnId);

    // Eğer rowValue null veya undefined ise eşleşme yok
    if (rowValue == null) return false;

    // String olarak karşılaştırma yap
    const rowValueString = String(rowValue).toLowerCase();
    const filterValueString = String(filterValue).toLowerCase();

    // Case-insensitive içerik eşleşmesi veya zayıf eşitlik
    return (
      rowValueString.includes(filterValueString) || rowValue === filterValue
    );
  };
  useEffect(() => {
    if (!rows.data) return;

    if (manualPagination) {
      // For manual pagination, only update if data actually changed
      setRowsData((prev) => {
        if (
          prev.data === rows.data &&
          prev.rowCount === rows.rowCount &&
          prev.pageCount === rows.pageCount
        ) {
          return prev;
        }
        return rows;
      });
    } else {
      const newRowCount = rows.rowCount || rows.data.length;
      const newPageCount =
        rows.pageCount || Math.ceil(newRowCount / pagination.pageSize);

      setRowsData((prev) => {
        if (
          prev.data === rows.data &&
          prev.rowCount === newRowCount &&
          prev.pageCount === newPageCount
        ) {
          return prev;
        }
        return {
          ...rows,
          data: rows.data,
          rowCount: newRowCount,
          pageCount: newPageCount,
        };
      });
    }
  }, [rows, pagination.pageSize, manualPagination]);

  useEffect(() => {
    setTableData({
      tableId,
      columnFilters,
      columnPinning,
      sorting,
      pagination,
      globalFilter,
      rowSelection,
    });
  }, [
    tableId,
    columnFilters,
    columnPinning,
    sorting,
    pagination,
    globalFilter,
    rowSelection,
    setTableData,
  ]);
  const table = useReactTable<T>({
    columns,
    data: rowsData.data,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    state: {
      columnFilters,
      columnPinning,
      sorting,
      pagination,
      globalFilter,
      rowSelection,
    },
    rowCount: rowsData.rowCount,
    onColumnPinningChange: setColumnPinning,
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onRowSelectionChange: setRowSelection,
    globalFilterFn: weakIncludesStringFn,
    manualPagination,
    enableColumnResizing: true,
    columnResizeMode: "onChange",
    defaultColumn: {
      minSize: 100,
      size: 100,
      enableResizing: true,
    },
    pageCount: rowsData.pageCount,
    manualFiltering: false,
  });

  const tableContainerRef = useRef<any>(null);
  const rowVirtualizer = useVirtualizer({
    count: Math.max(
      pagination.pageSize < table.getFilteredRowModel().rows.length
        ? pagination.pageSize
        : table.getFilteredRowModel().rows.length,
      0
    ),
    estimateSize: () => 52,
    getScrollElement: () => tableContainerRef.current,
    measureElement:
      typeof window !== "undefined" &&
      navigator.userAgent.indexOf("Firefox") === -1
        ? (element) => element?.getBoundingClientRect().height
        : undefined,
    overscan: 5,
  });
  const selectedRows = rowsData.data.filter((_, index) => rowSelection[index]);
  const actions = {
    selectedRows,
    pagination,
    setRowSelection,
    setGlobalFilter,
    setPagination,
    table,
  };
  const renderBulkActions = () => {
    if (typeof bulkActions === "function") {
      return bulkActions(actions);
    }
    return bulkActions;
  };

  const renderCardComponent = (row: Row<T>) => {
    if (typeof cardComponent === "function") {
      return cardComponent({ row });
    }
    return cardComponent;
  };

  const renderRightTop = () => {
    if (typeof rightTop === "function") {
      return rightTop(actions);
    }
    return rightTop;
  };

  const renderLeftTop = () => {
    if (typeof leftTop === "function") {
      return leftTop(actions);
    }
    return leftTop;
  };

  const renderCustomPagination = () => {
    if (typeof customPagination === "function") {
      return customPagination(actions);
    }
    return customPagination;
  };
  const { rows: rowsModel } = table.getPaginationRowModel();

  const virtualItems = rowVirtualizer.getVirtualItems();
  const virtualRows = useMemo(() => {
    return virtualItems.map((virtualRow) => {
      const row = rowsModel[virtualRow.index] as Row<T>;
      if (!row) return null;
      return {
        virtualRow,
        row,
        cells: row.getVisibleCells().map((cell) => {
          const header = table
            .getHeaderGroups()
            .flatMap((headerGroup) => headerGroup.headers)
            .find((h) => h.id === cell.column.id);
          return { cell, header: header as Header<T, unknown> };
        }),
      };
    });
  }, [virtualItems, rowsModel, table]);

  useEffect(() => {
    if (!manualPagination) {
      const filteredRowCount =
        table.getState().columnFilters.length === 0
          ? rows.data.length
          : table.getFilteredRowModel().rows.length;
      const newPageCount = Math.ceil(filteredRowCount / pagination.pageSize);
      setRowsData((prev) => {
        return {
          ...prev,
          rowCount: filteredRowCount,
          pageCount: newPageCount,
        };
      });
      if (pagination.pageIndex >= newPageCount) {
        setPagination((prev) => ({
          ...prev,
          pageIndex: Math.max(0, newPageCount - 1),
        }));
      }
    } else {
      setRowsData((prev) => {
        if (
          prev.data === rows.data &&
          prev.rowCount === rows.rowCount &&
          prev.pageCount === rows.pageCount
        ) {
          return prev;
        }
        return rows;
      });
    }
  }, [
    columnFilters,
    globalFilter,
    pagination.pageSize,
    table,
    pagination.pageIndex,
    manualPagination,
    rows,
  ]);

  return (
    <div className="w-full">
      <div className="rounded-2.5xl px-3 max-w-full grid relative h-full">
        {bulkActions && <BulkActions>{renderBulkActions()}</BulkActions>}
        <div className="flex flex-col gap-3 transition-none overflow-auto relative">
          {isLoading && <Loading />}
          <div
            className={`flex justify-between items-center ${
              !bulkActions && !manualSearch && !manualPagination ? "p-3" : ""
            }`}
          >
            <div className="flex items-center h-full flex-1">
              {!manualSearch && (
                <input
                  className="h-10 w-full max-w-72 rounded-lg px-3 border border-gray-300"
                  value={globalFilter}
                  onChange={(e) =>
                    table.setGlobalFilter(String(e.target.value))
                  }
                  placeholder="Ara"
                />
              )}
              {leftTop && (
                <div className="flex-1 flex justify-start items-center gap-2">
                  {renderLeftTop()}
                </div>
              )}
            </div>
            {rightTop && (
              <div className="flex-1 flex justify-end items-center gap-2">
                {renderRightTop()}
              </div>
            )}
          </div>
          <div
            ref={tableContainerRef}
            style={{
              minHeight: scrollable
                ? minHeight
                  ? minHeight
                  : "70%"
                : minHeight || "auto",
              maxHeight: scrollable
                ? `calc(100svh - ${pageOffset}rem)`
                : maxHeight &&
                  Number(maxHeight) > rowVirtualizer.getTotalSize() + 48
                ? maxHeight
                : `calc(${rowVirtualizer.getTotalSize()}px + 64px)`,
            }}
            className="overflow-auto max-sm:flex h-screen"
          >
            {viewMode === "table" && (
              <div
                className="caption-bottom text-sm flex-1 inline-table w-full relative"
                style={{ height: `${rowVirtualizer.getTotalSize()}px` }}
              >
                <div className="sticky top-0 h-12 z-20 w-full bg-zinc-100 rounded-t-xl">
                  {table.getHeaderGroups().map((headerGroup) => (
                    <div
                      className="transition-colors hover:text-zinc-800 data-[state=selected]:bg-white flex group divide-x sticky top-0"
                      key={headerGroup.id}
                    >
                      {headerGroup.headers.map((header) => (
                        <div
                          key={header.id}
                          className={cn(
                            "bg-gray-200 text-left align-middle font-medium text-base text-gray-700 flex items-center flex-shrink-0 last:rounded-r-lg first:rounded-l-lg last:flex-1 relative select-none min-h-12 group/head",
                            {
                              "cursor-pointer": header.column.getCanSort(),
                              "flex-1":
                                table.getAllColumns().length - 1 ===
                                header.index,
                            }
                          )}
                          style={{
                            width: header.getSize(),
                            ...getCommonPinningStyles(header.column),
                          }}
                        >
                          <div
                            className="flex max-sm:flex-col md:gap-3 sm:items-center sm:justify-between whitespace-nowrap relative w-full px-3"
                            onClick={
                              header.column.getCanSort()
                                ? header.column.getToggleSortingHandler()
                                : undefined
                            }
                            role="button"
                            tabIndex={0}
                          >
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                            {(header.column.getCanFilter() ||
                              header.column.getCanSort()) && (
                              <div className="flex items-center gap-1 md:pr-5 last:pr-0">
                                {header.column.getCanFilter() ? (
                                  <Filter
                                    column={header.column}
                                    table={table}
                                  />
                                ) : null}
                                {header.column.getCanSort() && (
                                  <div className="flex flex-col text-zinc-800">
                                    <ChevronDown
                                      className={cn(
                                        "translate-y-2 translate-x-1 size-5 text-gray-800",
                                        {
                                          "text-green-600":
                                            header.column.getIsSorted() ===
                                            "asc",
                                        }
                                      )}
                                    />
                                    <ChevronUp
                                      className={cn(
                                        "-translate-y-2 -translate-x-1 size-5 text-gray-800",
                                        {
                                          "text-green-600":
                                            header.column.getIsSorted() ===
                                            "desc",
                                        }
                                      )}
                                    />
                                  </div>
                                )}
                              </div>
                            )}
                          </div>

                          {header.column.getCanResize() &&
                            header.index !==
                              table.getAllColumns().length - 1 && (
                              <div
                                aria-hidden="true"
                                onMouseDown={header.getResizeHandler()}
                                onTouchStart={header.getResizeHandler()}
                                className={cn(
                                  "absolute right-0 top-0 flex justify-end h-full w-2 cursor-col-resize select-none touch-none "
                                )}
                              >
                                <div
                                  className={cn(
                                    "w-[1px] h-full bg-transparent peer-hover:bg-gray-200 group-hover/head:bg-gray-200 transition-all",
                                    {
                                      "bg-gray-200 hover:bg-gray-200":
                                        header.column.getIsResizing(),
                                    }
                                  )}
                                />
                              </div>
                            )}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
                {!isLoading &&
                  table.getFilteredRowModel().rows.length === 0 &&
                  rowsData.data.length === 0 && (
                    <>
                      {emptyContent ? (
                        emptyContent
                      ) : (
                        <div className="w-full bg-yellow-100 text-yellow-800 flex items-center p-2 gap-2">
                          <span className="flex items-center gap-2 sticky left-2">
                            <AlertTriangle />
                            Sonuç bulunamadı
                          </span>
                        </div>
                      )}
                    </>
                  )}

                {!isLoading && table.getFilteredRowModel().rows.length > 0 && (
                  <div className="grid relative">
                    {virtualRows.map((virtualItem, index) => {
                      if (!virtualItem) return null;
                      const { virtualRow, row, cells } = virtualItem;
                      return (
                        <div
                          className="will-change-transform transition-colors relative hover:text-zinc-800 data-[state=selected]:bg-white flex group"
                          key={row.id}
                          style={{
                            height: `${virtualRow.size}px`,
                            transformStyle: "preserve-3d",
                            backfaceVisibility: "hidden",
                            transform: `translateY(${
                              virtualRow.start - index * virtualRow.size
                            }px)`,
                          }}
                        >
                          {cells.map(({ cell, header }) => {
                            return (
                              <div
                                key={cell.id}
                                style={{
                                  width: cell.column.getSize(),
                                  ...getCommonPinningStyles(cell.column),
                                }}
                                className={cn(
                                  "align-middle text-gray-800 flex flex-shrink-0 bg-gray-50 group-hover:bg-gray-100 group-hover:text-zinc-800 last:flex-1 text-left group/cell",
                                  {
                                    "flex-1":
                                      table.getAllColumns().length - 1 ===
                                      cell.column.getIndex(),
                                  }
                                )}
                              >
                                <div className="min-h-12 text-sm flex items-center break-all px-3 w-full">
                                  {flexRender(
                                    cell.column.columnDef.cell,
                                    cell.getContext()
                                  )}
                                </div>
                                {cell.column.getCanResize() &&
                                  cell.column.getIndex() !==
                                    table.getAllColumns().length - 1 && (
                                    <div
                                      aria-hidden="true"
                                      onMouseDown={header.getResizeHandler()}
                                      onTouchStart={header.getResizeHandler()}
                                      className={cn(
                                        "absolute right-0 top-0 flex justify-end h-full w-2 cursor-col-resize select-none touch-none"
                                      )}
                                    >
                                      <div
                                        className={cn(
                                          "w-[1px] h-full bg-transparent peer-hover:bg-gray-800 group-hover/cell:bg-gray-800 transition-all",
                                          {
                                            "bg-gray-800 hover:bg-gray-800":
                                              cell.column.getIsResizing(),
                                          }
                                        )}
                                      />
                                    </div>
                                  )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            {viewMode === "card" && (
              <div className="p-3 w-full">
                <div className="grid grid-cols-2 gap-5">
                  {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                    const row = rowsModel[virtualRow.index] as Row<T>;
                    if (!row) return null;
                    return renderCardComponent(row);
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
        {!manualPagination && !customPagination && table && (
          <Pagination
            rows={rowsData}
            table={table}
            pagination={pagination}
            setPagination={setPagination}
          />
        )}
        {manualPagination && customPagination && renderCustomPagination()}
      </div>
    </div>
  );
};

export { CustomTable };
